import { supabase } from './supabase';

interface CrudOptions {
  orderBy: string;
  ascending: boolean;
  idColumn: string;
  stampUpdatedAt: boolean;
}

const DEFAULTS: CrudOptions = {
  orderBy: 'created_at',
  ascending: false,
  idColumn: 'id',
  stampUpdatedAt: true,
};

export function createCrudService<Row, Insert, Update>(
  table: string,
  options?: Partial<CrudOptions>
) {
  const opts: CrudOptions = { ...DEFAULTS, ...options };

  return {
    async getAll(): Promise<Row[]> {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .order(opts.orderBy, { ascending: opts.ascending });

      if (error) throw error;
      return (data || []) as Row[];
    },

    async getById(id: string): Promise<Row | null> {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq(opts.idColumn, id)
        .maybeSingle();

      if (error) throw error;
      return data as Row | null;
    },

    async create(payload: Insert): Promise<Row> {
      const { data, error } = await supabase
        .from(table)
        .insert(payload as never)
        .select()
        .single();

      if (error) throw error;
      return data as Row;
    },

    async update(id: string, payload: Update): Promise<Row> {
      const body = opts.stampUpdatedAt
        ? { ...(payload as object), updated_at: new Date().toISOString() }
        : payload;

      const { data, error } = await supabase
        .from(table)
        .update(body as never)
        .eq(opts.idColumn, id)
        .select()
        .single();

      if (error) throw error;
      return data as Row;
    },

    async delete(id: string): Promise<void> {
      const { error } = await supabase
        .from(table)
        .delete()
        .eq(opts.idColumn, id);

      if (error) throw error;
    },
  };
}
